import { useEffect, useRef, useState, type ComponentType } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import {
  ArrowLeft,
  Ban,
  BarChart3,
  BookOpenCheck,
  BookText,
  Bot,
  ChevronRight,
  CreditCard,
  FileWarning,
  Inbox,
  LayoutDashboard,
  ListChecks,
  Mail,
  ScrollText,
  Settings,
  Shield,
  SlidersHorizontal,
  Store,
  Upload,
  UserRound,
  Users,
  Waypoints,
} from "lucide-react";
import { useAuth } from "../auth/AuthContext";
import { TourProvider } from "../tutorial/Tour";
import { BAccountBanner, BBrand, BTopBar } from "../design/tokens";

type IconType = ComponentType<{ size?: number; strokeWidth?: number }>;

interface NavItem {
  to: string;
  label: string;
  icon: IconType;
  /** Pages in a menu that opens to the right of the sidebar (inline on phones). */
  children?: NavItem[];
}

export interface NavSection {
  heading: string;
  items: NavItem[];
}

export const USER_SECTIONS: NavSection[] = [
  {
    heading: "Workspace",
    items: [
      { to: "/app/dashboard", label: "Dashboard", icon: LayoutDashboard },
      // Receipts, bills and income.
      {
        to: "/app/receipts", label: "Ledger", icon: BookText,
        children: [{ to: "/app/review", label: "Bulk Review", icon: ListChecks }],
      },
      { to: "/app/upload", label: "Upload", icon: Upload },
      {
        to: "/app/reports", label: "Reporting", icon: BarChart3,
        children: [
          { to: "/app/vendors", label: "Vendors", icon: Store },
          { to: "/app/clients", label: "Clients", icon: UserRound },
          { to: "/app/cards", label: "Cards", icon: CreditCard },
        ],
      },
      {
        to: "/app/connections", label: "Inbox", icon: Inbox,
        children: [
          { to: "/app/connections", label: "Connections", icon: Mail },
          { to: "/app/inbox-activity", label: "Inbox activity", icon: ScrollText },
        ],
      },
    ],
  },
  {
    heading: "Integrations",
    items: [{ to: "/app/quickbooks", label: "QuickBooks", icon: BookOpenCheck }],
  },
  {
    heading: "Account",
    items: [{ to: "/app/settings", label: "Settings", icon: Settings }],
  },
];

export const ADMIN_SECTIONS: NavSection[] = [
  {
    heading: "Admin",
    items: [
      { to: "/admin/overview", label: "Overview", icon: LayoutDashboard },
      { to: "/admin/users", label: "Users", icon: Users },
      { to: "/admin/mailboxes", label: "Mailboxes", icon: Mail },
      { to: "/admin/messages", label: "Ingest log", icon: ScrollText },
      { to: "/admin/reports", label: "Parse reports", icon: FileWarning },
      { to: "/admin/ai-calls", label: "AI calls", icon: Bot },
    ],
  },
  {
    heading: "Configuration",
    items: [
      { to: "/admin/blocklist", label: "Sender blocklist", icon: Ban },
      { to: "/admin/settings", label: "System settings", icon: SlidersHorizontal },
    ],
  },
];

export default function AppLayout({ sections = USER_SECTIONS, mode = "app" }: { sections?: NavSection[]; mode?: "app" | "admin" }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [navOpen, setNavOpen] = useState(false);

  useEffect(() => setNavOpen(false), [location.pathname]);

  const userInitials = (user?.full_name || user?.email || "?")
    .split(/\s+/)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase() ?? "")
    .join("");

  // "Section / Page" for the top bar, from the longest matching nav route.
  // A sub-page's section is its parent (children listed first so they win ties).
  const current = sections
    .flatMap((s) =>
      s.items.flatMap((item) => [
        ...(item.children ?? []).map((child) => ({ section: item.label, item: child })),
        { section: s.heading, item },
      ]),
    )
    .filter(({ item }) => location.pathname.startsWith(item.to))
    .sort((a, b) => b.item.to.length - a.item.to.length)[0];
  const title = current ? (
    <>
      <span className="crumb-muted">{current.section}</span>
      <span className="crumb-sep">/</span>
      <span className="crumb-current">{current.item.label}</span>
    </>
  ) : null;

  const navLink = (item: NavItem) =>
    item.children ? <SideGroup key={item.label} item={item} /> : <SideLink key={item.to} item={item} />;

  return (
    <div className={`shell${navOpen ? " nav-open" : ""}`}>
      <aside className="side">
        <div className="side-brand">
          <BBrand to={mode === "admin" ? "/admin/overview" : "/app/dashboard"} size="sm" tone="dark" />
        </div>

        {mode === "admin" && (
          <div className="side-admin-flag">
            <Shield size={13} /> Administrator
          </div>
        )}

        <nav className="side-nav">
          {sections.map((section) => (
            <div key={section.heading}>
              <div className="side-heading">{section.heading}</div>
              <div className="side-group">{section.items.map(navLink)}</div>
            </div>
          ))}
        </nav>

        <div className="side-foot">
          {user?.is_admin &&
            (mode === "app"
              ? navLink({ to: "/admin/overview", label: "Admin console", icon: Waypoints })
              : navLink({ to: "/app/dashboard", label: "Back to workspace", icon: ArrowLeft }))}
          <BAccountBanner
            name={user?.full_name || user?.email || "Account"}
            subtitle={user?.full_name ? user.email : undefined}
            to="/app/settings"
          />
        </div>
      </aside>
      <div className="side-scrim" onClick={() => setNavOpen(false)} />

      <div className="shell-main">
        <BTopBar
          title={title}
          userInitials={userInitials}
          onLogout={logout}
          userTitle={user?.email}
          profileTo="/app/settings"
          onMenu={() => setNavOpen(true)}
        />
        <main className="shell-content">
          {mode === "app" ? (
            <TourProvider>
              <Outlet />
            </TourProvider>
          ) : (
            <Outlet />
          )}
        </main>
      </div>
    </div>
  );
}


function SideLink({ item }: { item: NavItem }) {
  const Icon = item.icon;
  return (
    <NavLink to={item.to} className={({ isActive }) => `side-link${isActive ? " active" : ""}`}>
      <Icon size={16} strokeWidth={1.9} />
      <span>{item.label}</span>
    </NavLink>
  );
}

/** A menu item whose pages open in a panel to the right of the sidebar, on
 *  hover or with the chevron (keyboard). On phones, where the sidebar is a
 *  drawer, they're listed inline under it instead. */
function SideGroup({ item }: { item: NavItem }) {
  const location = useLocation();
  const ref = useRef<HTMLDivElement>(null);
  const closeTimer = useRef<number | undefined>(undefined);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const children = item.children ?? [];
  const active = [item, ...children].some((c) => location.pathname.startsWith(c.to));
  const Icon = item.icon;

  const fromHover = useRef(false);
  const chevron = useRef<HTMLButtonElement>(null);
  const show = () => {
    window.clearTimeout(closeTimer.current);
    const row = ref.current?.getBoundingClientRect();
    const side = ref.current?.closest(".side")?.getBoundingClientRect();
    if (row && side) setPos({ top: row.top, left: side.right + 6 });
  };
  // A short delay lets the pointer cross the gap into the panel.
  const hide = () => {
    window.clearTimeout(closeTimer.current);
    closeTimer.current = window.setTimeout(() => setPos(null), 160);
  };

  useEffect(() => setPos(null), [location.pathname]);
  useEffect(() => () => window.clearTimeout(closeTimer.current), []);

  const links = (className: string) =>
    children.map((c) => {
      const ChildIcon = c.icon;
      return (
        <NavLink key={c.label} to={c.to} className={({ isActive }) => `${className}${isActive ? " active" : ""}`}>
          <ChildIcon size={15} strokeWidth={1.9} />
          <span>{c.label}</span>
        </NavLink>
      );
    });

  return (
    <div
      className="side-item"
      ref={ref}
      onMouseEnter={() => {
        fromHover.current = true;
        show();
      }}
      onMouseLeave={hide}
      onKeyDown={(e) => {
        if (e.key === "Escape" && pos) {
          setPos(null);
          chevron.current?.focus();
        }
      }}
      onBlur={(e) => !e.currentTarget.contains(e.relatedTarget as Node) && hide()}
    >
      <div className="side-row">
        <NavLink to={item.to} className={`side-link${active ? " active" : ""}`}>
          <Icon size={16} strokeWidth={1.9} />
          <span>{item.label}</span>
        </NavLink>
        <button
          type="button"
          className="side-expand"
          aria-label={`${item.label} pages`}
          aria-expanded={!!pos}
          ref={chevron}
          onClick={() => {
            // Hover already opened it: the click shouldn't snap it shut.
            if (pos && !fromHover.current) setPos(null);
            else show();
            fromHover.current = false;
          }}
        >
          <ChevronRight size={14} />
        </button>
      </div>
      {pos && (
        <div className="side-flyout" style={{ top: pos.top, left: pos.left }} onMouseEnter={show} onMouseLeave={hide}>
          <div className="side-flyout-title">{item.label}</div>
          {links("side-flyout-link")}
        </div>
      )}
      <div className="side-children">{links("side-link side-child")}</div>
    </div>
  );
}
